import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, type Received } from "./api";

/**
 * One fetch, four states, and the moment the answer arrived.
 *
 * `receivedAt` is not decoration. Every age on screen is anchored on it, so a
 * page left open over lunch shows a reading getting older rather than freezing
 * at whatever it said when it loaded.
 *
 * There is no cache and no deduplication. Eighty accounts is a small book and
 * a stale cache on a balance screen is worse than a second round trip.
 */
export type ResourceState<T> =
  | { status: "loading"; data: null; error: null; receivedAt: null }
  | { status: "ready"; data: T; error: null; receivedAt: number }
  | { status: "error"; data: T | null; error: ApiError; receivedAt: number | null };

export interface Resource<T> {
  status: "loading" | "ready" | "error";
  data: T | null;
  error: ApiError | null;
  receivedAt: number | null;
  /** Refetch without dropping back to the loading state. */
  reload: () => void;
  /** True while a reload is in flight over data that is already on screen. */
  refreshing: boolean;
}

export function useResource<T>(
  fetcher: () => Promise<Received<T>>,
  deps: unknown[] = [],
  /** Opt-in for a labeled coherent view; permission errors still clear it. */
  options: { keepPreviousData?: boolean } = {},
): Resource<T> {
  const [state, setState] = useState<ResourceState<T>>({
    status: "loading",
    data: null,
    error: null,
    receivedAt: null,
  });
  const [refreshing, setRefreshing] = useState(false);

  // Kept in a ref so changing the closure does not retrigger the effect --
  // callers write `() => api.accounts()` inline and a new function every
  // render would otherwise fetch forever.
  const keepPreviousDataRef = useRef(options.keepPreviousData === true);
  keepPreviousDataRef.current = options.keepPreviousData === true;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const generation = useRef(0);

  const run = useCallback(async (isReload: boolean) => {
    const mine = ++generation.current;
    if (isReload) setRefreshing(true);
    try {
      const { data, receivedAt } = await fetcherRef.current();
      // A slower earlier request must never overwrite a newer answer.
      if (mine !== generation.current) return;
      setState({ status: "ready", data, error: null, receivedAt });
    } catch (err) {
      if (mine !== generation.current) return;
      const error = err instanceof ApiError ? err : new ApiError(0, "Something went wrong loading this.");
      const retain = keepPreviousDataRef.current && error.status !== 401 && error.status !== 403;
      setState(previous => ({
        status: "error",
        data: retain ? previous.data : null,
        error,
        receivedAt: retain ? previous.receivedAt : null,
      }));
    } finally {
      if (mine === generation.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (keepPreviousDataRef.current) {
      setState(previous => previous.data === null
        ? { status: "loading", data: null, error: null, receivedAt: null }
        : { status: "ready", data: previous.data, error: null, receivedAt: previous.receivedAt ?? Date.now() });
      void run(true);
    } else {
      setState({ status: "loading", data: null, error: null, receivedAt: null });
      void run(false);
    }
    return () => { generation.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const reload = useCallback(() => {
    void run(true);
  }, [run]);

  return { ...state, reload, refreshing };
}

/**
 * Re-render on a timer so displayed ages keep counting up.
 *
 * Thirty seconds. Fast enough that "4 minutes ago" is never wrong by more than
 * half a minute, slow enough to be invisible.
 */
export function useTick(intervalMs = 30_000): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return tick;
}
